/**
 * Phones get the phone app (Andrew, 2026-10-03). Anyone who opens the console on a phone,
 * in any browser, is sent to the React Native app's web build, served from this same site
 * at /m/ (deploy-console.yml puts it there). Tablets and desktops keep the console.
 *
 * A phone is:
 *   - a touch screen (pointer: coarse) whose shorter side is under 600 CSS px, or
 *   - a browser that says it is a phone: iPhone or iPod, or "Mobile" (Android phones say
 *     "Android ... Mobile"; Android tablets leave Mobile out; an iPad says iPad).
 *
 * Staying on the console: open any console address with ?desktop=1 and this browser keeps
 * the console (localStorage "aithora.forceDesktop") until "Phone version" clears it.
 *
 * Runs once in main.tsx, before the app shell renders. Pure apart from the window it is
 * given, so it is tested without a browser.
 */

export const FORCE_DESKTOP_KEY = 'aithora.forceDesktop';
/** Where the phone app is served from on this site. */
export const PHONE_BASE = '/m/';

/**
 * Console addresses that never go to the phone app, and why:
 *   /delete-account   the public account-deletion page the app stores link to; it must
 *                     open exactly as published, signed in or out.
 *   /invite/<token>   invite links: the phone app has no invite or set-password screen.
 *   /request-access   the public access request: the phone app has no such screen.
 *   /guide            the Guide (its own pages, outside the shell): the phone app has none.
 *   /m/...            already the phone app (never loop).
 * Plus a single sign-on return (#session=... or #sso_error=... in the address): the phone
 * app has no single sign-on, so the console finishes it.
 */
export const EXEMPT_PREFIXES: readonly string[] = ['/delete-account', '/invite/', '/request-access', '/guide', '/m/'];

/** Old Flutter hash links (/#/invite/<token> and friends) open the same exempt pages. */
const EXEMPT_HASH = /^#\/(invite\/|request-access|delete-account)/;
const SSO_HASH = /(^#|&)(session|sso_error)=/;

export interface DeviceInfo {
  userAgent: string;
  /** matchMedia('(pointer: coarse)').matches */
  coarsePointer: boolean;
  screenWidth: number;
  screenHeight: number;
}

export function isPhone(d: DeviceInfo): boolean {
  const ua = d.userAgent || '';
  // iPad (and iPadOS desktop-mode Safari, which says Macintosh) stays: neither says Mobile
  // in the iPhone way, and the screen check below leaves it alone (768 px and wider).
  if (/iPhone|iPod/i.test(ua)) return true;
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) return true;
  if (/\bMobile\b/i.test(ua) && !/iPad|Tablet/i.test(ua)) return true;
  const shorter = Math.min(d.screenWidth || 0, d.screenHeight || 0);
  return d.coarsePointer && shorter > 0 && shorter < 600;
}

export function isExemptPath(pathname: string, hash = ''): boolean {
  const p = pathname || '/';
  if (p === '/m') return true;
  for (const pre of EXEMPT_PREFIXES) {
    const bare = pre.endsWith('/') ? pre.slice(0, -1) : pre;
    if (p === bare || p.startsWith(bare + '/')) return true;
  }
  if (EXEMPT_HASH.test(hash)) return true;
  if (SSO_HASH.test(hash)) return true;
  return false;
}

/** The phone app address for a console address (only sign-in has a matching screen). */
export function phonePathFor(pathname: string): string {
  if (pathname === '/signin' || pathname.startsWith('/signin/')) return PHONE_BASE + 'sign-in';
  return PHONE_BASE;
}

function safeLocal(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function readForceDesktop(store: Storage | null = safeLocal()): boolean {
  try {
    return store?.getItem(FORCE_DESKTOP_KEY) === '1';
  } catch {
    return false;
  }
}

export function setForceDesktop(on: boolean, store: Storage | null = safeLocal()): void {
  try {
    if (!store) return;
    if (on) store.setItem(FORCE_DESKTOP_KEY, '1');
    else store.removeItem(FORCE_DESKTOP_KEY);
  } catch {
    /* storage refused: the choice lasts only for this page */
  }
}

export interface Place {
  pathname: string;
  search: string;
  hash: string;
}

/**
 * Where this visit should go: a /m/ address, or null to stay on the console.
 * ?desktop=1 sets the stay-on-the-console flag (and stays).
 */
export function phoneRedirectTarget(place: Place, device: DeviceInfo, store: Storage | null = safeLocal()): string | null {
  const q = new URLSearchParams(place.search || '');
  if (q.get('desktop') === '1') {
    setForceDesktop(true, store);
    return null;
  }
  if (isExemptPath(place.pathname, place.hash)) return null;
  if (readForceDesktop(store)) return null;
  if (!isPhone(device)) return null;
  return phonePathFor(place.pathname);
}

export function deviceInfo(): DeviceInfo {
  let coarse = false;
  try {
    coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  } catch {
    coarse = false;
  }
  return {
    userAgent: navigator.userAgent,
    coarsePointer: coarse,
    screenWidth: window.screen?.width ?? 0,
    screenHeight: window.screen?.height ?? 0,
  };
}

/**
 * Called once before the app renders. Returns true when the browser is being sent to the
 * phone app (the caller then renders nothing). The address is replaced, so Back does not
 * bounce the person to the console and straight back again.
 */
export function redirectPhoneIfNeeded(): boolean {
  if (typeof window === 'undefined') return false;
  const target = phoneRedirectTarget(
    { pathname: window.location.pathname, search: window.location.search, hash: window.location.hash },
    deviceInfo(),
  );
  if (!target) return false;
  window.location.replace(target);
  return true;
}

/** True when this browser is a phone that chose the console (the "full console" notice). */
export function phoneOnFullConsole(): boolean {
  if (typeof window === 'undefined') return false;
  return readForceDesktop() && isPhone(deviceInfo());
}

/** "Phone version": forget the choice and open the phone app. */
export function goToPhoneVersion(): void {
  setForceDesktop(false);
  try {
    window.location.assign(PHONE_BASE);
  } catch {
    /* not in a browser */
  }
}
